import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

export default function ObjectionHandling() {
  const canonical = 'https://earnmyedge.com/objection-handling-training';
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How do I practice common sales objections?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Use Objection Dojo to roleplay objections with AI. You will receive feedback on structure, tone, and closing technique with a sample response.'
        }
      },
      {
        '@type': 'Question',
        name: 'Which frameworks are supported for objection handling?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'SPIN, Sandler, Challenger, MEDDIC, NEPQ, GAP, Straight Line, and Value Selling are all integrated with drills and scenarios.'
        }
      },
      {
        '@type': 'Question',
        name: 'Can I track improvement over time?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Scores are saved to your profile, with XP awarded and mastery tracked over time.'
        }
      },
      {
        '@type': 'Question',
        name: 'Do teams get role-based access and analytics?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Earn My Edge supports team accounts with roles (owner, manager, member) and daily analytics for teams and individual members.'
        }
      },
      {
        '@type': 'Question',
        name: 'What pricing is available?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'One Elite subscription (monthly or annual) and a limited Lifetime offer. See pricing on the landing page.'
        }
      }
    ]
  };

  const howToLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'Practice a sales objection with AI roleplay',
    description: 'Roleplay an objection and receive feedback and a sample response with Earn My Edge.',
    step: [
      { '@type': 'HowToStep', text: 'Open Objection Dojo.' },
      { '@type': 'HowToStep', text: 'Select a common objection or add your own.' },
      { '@type': 'HowToStep', text: 'Run the roleplay and deliver your response.' },
      { '@type': 'HowToStep', text: 'Review AI feedback on clarity, confidence, control, and close.' },
      { '@type': 'HowToStep', text: 'Compare with the sample response and iterate.' }
    ]
  };

  return (
    <main className="mx-auto max-w-4xl px-6 py-12">
      <Helmet>
        <title>Objection Handling Training | Earn My Edge</title>
        <meta name="description" content="Practice objection handling with AI roleplay and receive targeted feedback, sample responses, and mastery tracking." />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Objection Handling Training | Earn My Edge" />
        <meta property="og:description" content="Practice objection handling with AI roleplay and receive targeted feedback, sample responses, and mastery tracking." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="/assets/social-card.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Objection Handling Training | Earn My Edge" />
        <meta name="twitter:description" content="Practice objection handling with AI roleplay and receive targeted feedback, sample responses, and mastery tracking." />
        <meta name="twitter:image" content="/assets/social-card.png" />
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(howToLd)}</script>
      </Helmet>

      <header className="mb-8">
        <h1 className="text-3xl font-semibold">Master Objection Handling</h1>
        <p className="mt-4 text-muted-foreground">
          Turn stalls and pushback into progress. Practice real objections and refine your strategy and delivery.
        </p>
      </header>

      <section className="space-y-6">
        <div>
          <h2 className="text-xl font-medium">Roleplay Real Objections</h2>
          <p className="mt-2">
            Choose an objection, run a roleplay, and get clear feedback with a better sample response.
          </p>
          <div className="mt-3">
            <Link to="/app/objection-dojo" className="text-primary underline">Practice in Objection Dojo</Link>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-medium">Framework Strategies</h2>
          <p className="mt-2">
            Learn how SPIN, Sandler, Challenger, and MEDDIC approach objections — then test those strategies in scenarios.
          </p>
          <div className="mt-3">
            <Link to="/sales-frameworks" className="text-primary underline">Explore Framework Guides</Link>
          </div>
        </div>
      </section>

      <footer className="mt-10 border-t pt-6">
        <p className="font-medium">Related pages</p>
        <ul className="mt-2 list-disc pl-6">
          <li><Link to="/sales-training" className="underline">Sales Training</Link></li>
          <li><Link to="/ai-sales-coach" className="underline">AI Sales Coach</Link></li>
          <li><Link to="/sales-frameworks" className="underline">Sales Framework Guides</Link></li>
          <li><Link to="/sales-roleplay-scenarios" className="underline">Sales Roleplay Scenarios</Link></li>
        </ul>
      </footer>
    </main>
  );
}
